/**
 * SB5-B.1 — Contracts list page (`/contracts`).
 *
 * Net-new page from `.feedback/26-05-22 AFTER SB4/02-page-contracts.md`.
 * Uses the canonical `DataPageShell` template per
 * `.vibe/60_TEMPLATE_DATA_PAGE.md`. Largely a delta over
 * `ProjectsPage.tsx`.
 *
 * Filter order (locked top-to-bottom):
 *   1. Search (contract_code, project_name, site_name, customer,
 *      contractor, billing_contact.company_name, customer_reference,
 *      external_ids JSONB — BE-side per Q3)
 *   2. Projects multiselect
 *   3. Sites multiselect
 *   4. Customers multiselect
 *   5. Contractors multiselect
 *   6. States multiselect
 *   7. Show completed toggle (default OFF — hides COMPLETED/WITHDRAWN/REJECTED)
 *
 * Add + Edit are stubs that surface the shared `<ComingSoonDialog>`
 * per Q12; no edit/create modal in this story.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import type { GridSortModel } from "@mui/x-data-grid";
import {
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  SearchField,
  TypeaheadChipFilter,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
} from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import { ComingSoonDialog } from "../shared/index.js";
import {
  useContractsContractorsFacetQuery,
  useContractsCustomersFacetQuery,
  useContractsProjectsFacetQuery,
  useContractsSitesFacetQuery,
} from "./useContracts.js";
import { useOwnerMemberContracts } from "./useOwnerMemberContracts.js";
import {
  CONTRACT_STATES,
  CONTRACTS_SORT_KEYS,
  type ContractListItem,
  type ContractState,
  type ContractsSortDir,
  type ContractsSortKey,
  type ListContractsParams,
} from "./types.js";
import { contractStateLabel } from "./ContractStatusChip.js";
import { buildContractColumns } from "./columns.js";
import { FileTextIcon, PlusIcon } from "@aeriosiq/ui/icons";

const STATE_OPTIONS: readonly { id: ContractState; name: string }[] =
  CONTRACT_STATES.map((s) => ({ id: s, name: contractStateLabel(s) }));

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SORT_BY: ContractsSortKey = "contractCode";
const DEFAULT_SORT_DIR: ContractsSortDir = "desc";

const COMING_SOON_TITLE = "Coming soon";
const COMING_SOON_BODY =
  "Contract create / edit will be available in a future release. Contracts are currently created automatically together with projects.";

function parseSort(
  value: string | null,
): { key: ContractsSortKey; dir: ContractsSortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  if (!(CONTRACTS_SORT_KEYS as readonly string[]).includes(k)) return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key: k as ContractsSortKey, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

function parseCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v.length > 0) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

export function ContractsPage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const includeCompleted = params.get("includeCompleted") === "1";
  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  // Per Q12: gate Add button on the same role set as project.create
  // (tenant_admin | project_manager | platform_admin). The matrix key
  // SB6-CTR-01 — Add Contract button visibility. `contract.create`
  // doesn't exist in the role-matrix yet; we reuse `project.create`
  // (same role set per the SB5 brief) but MUST use the resource-aware
  // helper because the matrix cell is `"conditional"` for tenant_admin
  // / project_manager (predicate: caller.tenantId === resource.tenantId).
  // The unconditional `useCanEditResource` always returns false on a
  // conditional cell, which is why Christian saw NO Add Contract button
  // on 2026-05-24 even though he is tenant_admin [00:30:52].
  // Without a specific resource (the list page is in the user's own
  // tenant) the predicate's same-tenant invariant holds by definition.
  const canCreate = useCanEditResourceFor("project.create");

  // Mirror search → URL with ref-guarded effect per
  // `60_TEMPLATE_DATA_PAGE.md` §"Pagination — search-effect ref-
  // instability bug".
  const lastAppliedSearchRef = useRef(params.get("search") ?? "");
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const projectSelected = useMemo(
    () => parseCsv(params.get("project")),
    [params],
  );
  const siteSelected = useMemo(() => parseCsv(params.get("site")), [params]);
  const customerSelected = useMemo(
    () => parseCsv(params.get("customer")),
    [params],
  );
  const contractorSelected = useMemo(
    () => parseCsv(params.get("contractor")),
    [params],
  );
  const stateSelected = useMemo<ContractState[]>(() => {
    const raw = parseCsv(params.get("state"));
    return raw.filter((s): s is ContractState =>
      (CONTRACT_STATES as readonly string[]).includes(s),
    );
  }, [params]);

  const queryParams = useMemo<ListContractsParams>(() => {
    const out: ListContractsParams = { page, pageSize };
    if (debouncedSearch.length > 0) out.q = debouncedSearch;
    out.sortBy = sortParsed?.key ?? DEFAULT_SORT_BY;
    out.sortDir = sortParsed?.dir ?? DEFAULT_SORT_DIR;
    out.includeCompleted = includeCompleted;
    if (projectSelected.length > 0) out.projectIds = projectSelected;
    if (siteSelected.length > 0) out.siteIds = siteSelected;
    if (customerSelected.length > 0) out.customerIds = customerSelected;
    if (contractorSelected.length > 0) out.contractorIds = contractorSelected;
    if (stateSelected.length > 0) out.states = stateSelected;
    return out;
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    includeCompleted,
    projectSelected,
    siteSelected,
    customerSelected,
    contractorSelected,
    stateSelected,
  ]);

  // TNCY-6b §b — Contracts is the second merged owner/member list (after
  // Projects). The BE supports `scope=member` + ownership metadata on
  // `ContractListItem` (projects/v1@2.4.0 / TNCY-4), so this fires both
  // scopes and merges client-side; member rows carry the `<OwnershipBadge>`.
  const {
    rows: mergedRows,
    total,
    isPending,
    isError,
  } = useOwnerMemberContracts(queryParams);
  const { data: projectsFacet } = useContractsProjectsFacetQuery();
  const { data: sitesFacet } = useContractsSitesFacetQuery();
  const { data: customersFacet } = useContractsCustomersFacetQuery();
  const { data: contractorsFacet } = useContractsContractorsFacetQuery();
  const projectOptions = projectsFacet?.items ?? [];
  const siteOptions = sitesFacet?.items ?? [];
  const customerOptions = customersFacet?.items ?? [];
  const contractorOptions = contractorsFacet?.items ?? [];

  function setSelectedFor(queryKey: string, values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete(queryKey);
        else next.set(queryKey, canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setStateSelected(values: readonly string[]): void {
    const canonical = [...new Set(values)]
      .filter((v): v is ContractState =>
        (CONTRACT_STATES as readonly string[]).includes(v),
      )
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) next.delete("state");
        else next.set("state", canonical.join(","));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function toggleIncludeCompleted(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) next.set("includeCompleted", "1");
        else next.delete("includeCompleted");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) next.delete("page");
        else next.set("page", String(p));
        return next;
      },
      { replace: true },
    );
  }
  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) next.delete("pageSize");
        else next.set("pageSize", String(n));
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  const sortModel = useMemo<GridSortModel>(() => {
    const key = sortParsed?.key ?? DEFAULT_SORT_BY;
    const dir = sortParsed?.dir ?? DEFAULT_SORT_DIR;
    return [{ field: key, sort: dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!(CONTRACTS_SORT_KEYS as readonly string[]).includes(head.field)) {
        return;
      }
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${head.field}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const openComingSoon = useCallback(() => {
    setComingSoonOpen(true);
  }, []);
  const closeComingSoon = useCallback(() => {
    setComingSoonOpen(false);
  }, []);

  const columns = useMemo(
    () =>
      buildContractColumns({
        onEdit: openComingSoon,
        // R0627-05 — origin column always on (house icon for own rows too).
        showOwnership: true,
      }),
    [openComingSoon],
  );

  const rows = mergedRows;

  return (
    <DataPageShell title="Contracts">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Contracts toolbar"
        filtersTestId="contracts-toolbar-filters"
        settingsTestId="contracts-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search contracts"
            />
            <TypeaheadChipFilter
              label="Projects"
              value={projectSelected}
              onChange={(next) => {
                setSelectedFor("project", next);
              }}
              options={projectOptions}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            <TypeaheadChipFilter
              label="Sites"
              value={siteSelected}
              onChange={(next) => {
                setSelectedFor("site", next);
              }}
              options={siteOptions}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            <TypeaheadChipFilter
              label="Customers"
              value={customerSelected}
              onChange={(next) => {
                setSelectedFor("customer", next);
              }}
              options={customerOptions}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            <TypeaheadChipFilter
              label="Contractors"
              value={contractorSelected}
              onChange={(next) => {
                setSelectedFor("contractor", next);
              }}
              options={contractorOptions}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.label}
            />
            {/* SB6-CONS-03 — eagerOptions reveals the full enum on
                open; ContractState is a small fixed list so typing-to-
                search is more friction than help. */}
            <TypeaheadChipFilter
              label="States"
              eagerOptions
              value={stateSelected}
              onChange={setStateSelected}
              options={STATE_OPTIONS}
              getOptionId={(o) => o.id}
              getOptionLabel={(o) => o.name}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeCompleted}
                  onChange={(_e, c) => {
                    toggleIncludeCompleted(c);
                  }}
                  inputProps={{ "aria-label": "Show completed contracts" }}
                />
              }
              label="Show completed"
              sx={{ ml: 0, mr: 0 }}
            />
          </>
        }
        settings={
          <>
            <Box sx={{ flex: 1 }} />
            {canCreate ? (
              <Button
                aria-label="Add Contract"
                onClick={openComingSoon}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                {/* SB6-D1 — text per Christian's decision: "Add Contract". */}
                Add Contract
              </Button>
            ) : null}
          </>
        }
      />

      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load contracts.
        </Typography>
      ) : rows.length === 0 ? (
        <ContractsEmptyState />
      ) : (
        <DataTable<ContractListItem & { id: string }>
          rows={rows.map((r) => ({ ...r, id: r.contractId }))}
          columns={columns as never}
          ariaLabel="Contracts"
          getRowId={(row) => row.id}
          rowCount={total}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pinnedActionColumn
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="contracts"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="contracts-footer-total"
      />

      <ComingSoonDialog
        open={comingSoonOpen}
        onClose={closeComingSoon}
        title={COMING_SOON_TITLE}
        body={COMING_SOON_BODY}
      />
    </DataPageShell>
  );
}

function ContractsEmptyState(): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <FileTextIcon
        sx={{ fontSize: 48, color: "text.secondary" }}
        aria-hidden
      />
      <Typography variant="h6" color="text.primary">
        No contracts yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Contracts appear here once they are created for your tenant.
      </Typography>
    </Box>
  );
}
